import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { ENV } from '../config/env';
import OfflineJobCard from '../components/OfflineJobCard';
import OfflineJobForm from '../components/OfflineJobForm';
import SubscriptionBanner from '../components/SubscriptionBanner';
import { 
  Plus, 
  Filter, 
  Download, 
  TrendingUp, 
  IndianRupee,
  Calendar,
  ExternalLink,
  BarChart3,
  PieChart,
  ArrowUpRight,
  ArrowDownRight,
  CheckCircle,
  Sparkles,
  Tag,
  TrendingUp as TrendUp,
  FileText,
  Zap
} from 'lucide-react';

const OfflineJobs = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const offlinePrice = ENV.SUBSCRIPTION.OFFLINE_JOBS_MONTHLY;
  const { offlineJobs, checkFeatureAccess, deleteOfflineJob } = useData();
  const [showForm, setShowForm] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [filterMonth, setFilterMonth] = useState('all');
  const [viewMode, setViewMode] = useState('all'); // 'all', 'platform', 'offline'
  const [hasAccess, setHasAccess] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);

  // Check feature access on mount and when user changes
  useEffect(() => {
    const checkAccess = async () => {
      if (!user) {
        setHasAccess(false);
        setCheckingAccess(false);
        return;
      }
      
      setCheckingAccess(true);
      const userId = user.uid || user.id;
      console.log('🔐 Checking offline jobs access for user:', userId, 'Full user:', user);
      const access = await checkFeatureAccess(userId, 'offline_jobs');
      console.log('🔐 Access result:', access);
      setHasAccess(access);
      setCheckingAccess(false);
    };
    
    checkAccess();
  }, [user, checkFeatureAccess]);

  // Filter user's jobs
  const userId = user?.uid || user?.id;
  const userJobs = offlineJobs.filter(job => job.userId === userId);

  // Apply filters
  const filteredJobs = useMemo(() => {
    let filtered = [...userJobs];

    if (filterType !== 'all') {
      filtered = filtered.filter(job => job.serviceType === filterType);
    }

    if (filterMonth !== 'all') {
      filtered = filtered.filter(job => {
        const jobMonth = new Date(job.jobDate).toISOString().slice(0, 7);
        return jobMonth === filterMonth;
      });
    }

    return filtered.sort((a, b) => new Date(b.jobDate) - new Date(a.jobDate));
  }, [userJobs, filterType, filterMonth]);

  // Calculate statistics
  const stats = useMemo(() => {
    const totalJobs = filteredJobs.length;
    const totalAmount = filteredJobs.reduce((sum, job) => sum + job.amount + (job.taxAmount || 0), 0);
    const currentMonth = new Date().toISOString().slice(0, 7);
    const thisMonthJobs = filteredJobs.filter(job => 
      new Date(job.jobDate).toISOString().slice(0, 7) === currentMonth
    );
    const thisMonthAmount = thisMonthJobs.reduce((sum, job) => sum + job.amount + (job.taxAmount || 0), 0);

    // Service type breakdown
    const serviceBreakdown = filteredJobs.reduce((acc, job) => {
      acc[job.serviceType] = (acc[job.serviceType] || 0) + 1;
      return acc;
    }, {});

    const mostCommonService = Object.keys(serviceBreakdown).reduce((a, b) => 
      serviceBreakdown[a] > serviceBreakdown[b] ? a : b, 'boarding'
    );

    return {
      totalJobs,
      totalAmount,
      thisMonthJobs: thisMonthJobs.length,
      thisMonthAmount,
      averageAmount: totalJobs > 0 ? totalAmount / totalJobs : 0,
      mostCommonService,
      serviceBreakdown
    };
  }, [filteredJobs]);

  const handleEdit = (job) => {
    setEditingJob(job);
    setShowForm(true);
  };

  const handleDelete = (jobId) => {
    if (window.confirm('Are you sure you want to delete this job entry?')) {
      deleteOfflineJob(jobId);
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingJob(null);
  };

  const handleExport = () => {
    // Simple CSV export
    const headers = ['Date', 'Service Type', 'Pet Name', 'Client', 'Duration', 'Amount', 'Tax', 'Total', 'Payment Method', 'Notes'];
    const rows = filteredJobs.map(job => [
      new Date(job.jobDate).toLocaleDateString('en-IN'),
      job.serviceType,
      job.petName,
      job.clientName,
      `${job.duration} ${job.durationUnit}`,
      job.amount,
      job.taxAmount || 0,
      job.amount + (job.taxAmount || 0),
      job.paymentMethod,
      job.notes || ''
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `offline-jobs-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  const serviceTypeLabels = {
    boarding: 'Boarding',
    walking: 'Walking',
    grooming: 'Grooming',
    sitting: 'Pet Sitting',
    training: 'Training',
    other: 'Other'
  };

  // Get unique months from jobs
  const availableMonths = useMemo(() => {
    const months = [...new Set(userJobs.map(job => 
      new Date(job.jobDate).toISOString().slice(0, 7)
    ))].sort().reverse();
    return months;
  }, [userJobs]);

  // Show loading state while checking access
  if (checkingAccess) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 pb-32 lg:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl p-12 text-center shadow-sm">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Checking access...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-[var(--pf-canvas)] pt-20 pb-32 lg:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Hero Section */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--pf-accent-soft)] text-[var(--pf-accent)] rounded-full text-sm font-semibold mb-6">
              <Sparkles className="w-4 h-4" />
              Professional Job Tracking
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Track Your <span className="text-[var(--pf-accent)]">Offline Jobs</span><br />
              Like a Pro
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Manage all your external pet care bookings, track payments, generate invoices, 
              and get insights into your complete business performance, all in one place.
            </p>
          </div>

          {/* Feature Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
            <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-[var(--pf-border)] hover:border-[var(--pf-accent)] transition-all transform hover:-translate-y-1">
              <div className="w-14 h-14 bg-[var(--pf-accent)] rounded-xl flex items-center justify-center mb-4">
                <BarChart3 className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Comprehensive Tracking</h3>
              <p className="text-gray-600">
                Log every detail: service type, client info, pet details, amounts, payment methods, and custom notes.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-blue-100 hover:border-blue-300 transition-all transform hover:-translate-y-1">
              <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center mb-4">
                <TrendUp className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Advanced Analytics</h3>
              <p className="text-gray-600">
                Compare online vs offline performance, analyze revenue trends, and identify your most profitable services.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-green-100 hover:border-green-300 transition-all transform hover:-translate-y-1">
              <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center mb-4">
                <FileText className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Export & Reports</h3>
              <p className="text-gray-600">
                Download CSV reports for accounting, generate tax documents, and keep records organized effortlessly.
              </p>
            </div>
          </div>

          {/* Pricing Card - Highlighted */}
          <div className="max-w-2xl mx-auto mb-12">
            <div className="relative pf-organic-card pf-organic-card--dark rounded-3xl shadow-2xl overflow-hidden">
              {/* Decorative elements */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32"></div>
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full -ml-24 -mb-24"></div>
              
              <div className="relative p-10 md:p-12 text-white">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-3xl md:text-4xl font-bold mb-2">Offline Jobs Pro</h2>
                    <p className="text-blue-100/80 text-lg">Complete job tracking solution</p>
                  </div>
                  <div className="text-right">
                    <div className="text-5xl md:text-6xl font-bold">₹{offlinePrice}</div>
                    <div className="text-blue-100/70">/month</div>
                  </div>
                </div>

                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-green-300 flex-shrink-0 mt-1" />
                    <div>
                      <span className="font-semibold text-white">Unlimited Job Entries</span>
                      <p className="text-blue-100/80 text-sm">Track as many offline jobs as you want</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-green-300 flex-shrink-0 mt-1" />
                    <div>
                      <span className="font-semibold text-white">Revenue Analytics & Insights</span>
                      <p className="text-blue-100/80 text-sm">Compare platform vs offline performance</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-green-300 flex-shrink-0 mt-1" />
                    <div>
                      <span className="font-semibold text-white">Export to CSV</span>
                      <p className="text-blue-100/80 text-sm">Download reports for accounting & taxes</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-green-300 flex-shrink-0 mt-1" />
                    <div>
                      <span className="font-semibold text-white">Receipt Management</span>
                      <p className="text-blue-100/80 text-sm">Store and organize all job receipts</p>
                    </div>
                  </div>
                </div>

                {/* Free Trial CTA */}
                <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-6 mb-6 border-2 border-white/30">
                  <div className="flex items-center gap-3 mb-3">
                    <Tag className="w-6 h-6 text-yellow-300" />
                    <span className="text-xl font-bold text-yellow-300">Start Your Free 7-Day Trial!</span>
                  </div>
                  <p className="text-blue-100/90 mb-4">
                    Use coupon code <span className="font-mono font-bold text-xl text-yellow-300 bg-white/20 px-3 py-1 rounded-lg">TRAIL7</span> to try Offline Jobs Pro free for 7 days. No credit card required!
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button 
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText('TRAIL7');
                        const settingsPath =
                          user?.role === 'caregiver' ? '/caregiver-settings' : '/owner-settings';
                        navigate(`${settingsPath}?tab=subscriptions`);
                      }}
                      className="flex-1 bg-white text-[var(--pf-accent)] px-8 py-4 rounded-xl font-bold text-lg hover:bg-blue-50 transition-all shadow-lg flex items-center justify-center gap-2"
                    >
                      <Zap className="w-5 h-5" />
                      Copy Code & Activate
                    </button>
                  </div>
                </div>

                <p className="text-center text-blue-100/80 text-sm">
                  💡 After trial ends, continue for just ₹{offlinePrice}/month. Cancel anytime.
                </p>
              </div>
            </div>
          </div>

          {/* Additional Benefits */}
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg max-w-4xl mx-auto">
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-8">
              Why Caregivers Love Offline Jobs Tracking
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex gap-4">
                <div className="w-10 h-10 bg-[var(--pf-accent-soft)] rounded-lg flex items-center justify-center flex-shrink-0">
                  <IndianRupee className="w-5 h-5 text-[var(--pf-accent)]" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Complete Financial Picture</h4>
                  <p className="text-gray-600 text-sm">See all your earnings in one dashboard, both platform and offline jobs.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Never Lose Track</h4>
                  <p className="text-gray-600 text-sm">All your external bookings logged with dates, amounts, and client details.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Business Intelligence</h4>
                  <p className="text-gray-600 text-sm">Understand which services are most profitable and where to focus.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Tax Season Ready</h4>
                  <p className="text-gray-600 text-sm">Export all records to CSV for easy tax filing and accounting.</p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20 pb-32 lg:pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Subscription Status */}
        <SubscriptionBanner />

        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Offline Jobs</h1>
              <p className="text-gray-600">Track external pet care jobs and services</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleExport}
                disabled={filteredJobs.length === 0}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download className="w-5 h-5" />
                Export CSV
              </button>
              <button
                onClick={() => setShowForm(true)}
                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg hover:from-blue-600 hover:to-cyan-600 font-medium transition-colors"
              >
                <Plus className="w-5 h-5" />
                Add Offline Job
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center">
                <Calendar className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Jobs</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalJobs}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center">
                <IndianRupee className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total {user.role === 'caregiver' ? 'Revenue' : 'Spend'}</p>
                <p className="text-2xl font-bold text-gray-900">₹{stats.totalAmount.toLocaleString()}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[var(--pf-accent)] flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-gray-600">This Month</p>
                <p className="text-2xl font-bold text-gray-900">₹{stats.thisMonthAmount.toLocaleString()}</p>
                <p className="text-xs text-gray-500">{stats.thisMonthJobs} jobs</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-orange-500 to-red-500 flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Average Amount</p>
                <p className="text-2xl font-bold text-gray-900">₹{Math.round(stats.averageAmount).toLocaleString()}</p>
                <p className="text-xs text-gray-500">{serviceTypeLabels[stats.mostCommonService]}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm mb-6">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Filters:</span>
            </div>
            
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Services</option>
              {Object.entries(serviceTypeLabels).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>

            <select
              value={filterMonth}
              onChange={(e) => setFilterMonth(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Months</option>
              {availableMonths.map(month => (
                <option key={month} value={month}>
                  {new Date(month + '-01').toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
                </option>
              ))}
            </select>

            {(filterType !== 'all' || filterMonth !== 'all') && (
              <button
                onClick={() => {
                  setFilterType('all');
                  setFilterMonth('all');
                }}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>

        {/* Jobs List */}
        {filteredJobs.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredJobs.map(job => (
              <OfflineJobCard
                key={job.id}
                job={job}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-xl p-12 text-center shadow-sm">
            <ExternalLink className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Offline Jobs Yet</h3>
            <p className="text-gray-600 mb-6">
              {filterType !== 'all' || filterMonth !== 'all' 
                ? 'No jobs match your current filters. Try adjusting them.'
                : 'Start tracking your external pet care jobs by adding your first entry.'}
            </p>
            {(filterType !== 'all' || filterMonth !== 'all') ? (
              <button
                onClick={() => {
                  setFilterType('all');
                  setFilterMonth('all');
                }}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
              >
                Clear Filters
              </button>
            ) : (
              <button
                onClick={() => setShowForm(true)}
                className="px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg hover:from-blue-600 hover:to-cyan-600 font-medium transition-colors"
              >
                Add Your First Job
              </button>
            )}
          </div>
        )}

        {/* Form Modal */}
        {showForm && (
          <OfflineJobForm
            onClose={handleCloseForm}
            initialData={editingJob}
            onSuccess={() => {
              // Refresh happens automatically due to state management
            }}
          />
        )}
      </div>
    </div>
  );
};

export default OfflineJobs;

